import React, { useEffect, useId, useMemo, useState } from "react";
import { Hand, MessageCircleQuestion } from "lucide-react";
import PendingApproval from "./components/PendingApproval";
import PendingQuestion from "./components/PendingQuestion";
import QuestionSteps from "./components/QuestionSteps";
import useQuestionPicks from "./hooks/useQuestionPicks";
import { questionsOf, type AskQuestion } from "./askUser";
import { cn } from "@/shared/lib/cn";
import type { PausedRun, PendingReply } from "./types";

type DockTabId = "approvals" | "questions";

/**
 * The dock stacked behind the composer while a server run waits on the person:
 * its approval under Approvals, or its questions under Questions, each a tab on a
 * strip that tucks under the composer (the "peeking layer" pattern). Several
 * questions show as steps on the strip. The card is answered through onAnswer and
 * the run ended by onStop.
 */
export default function ComposerDock({ agentName, paused = null, onAnswer, onStop }: {
  agentName?: string;
  paused?: PausedRun | null;
  onAnswer?: (reply: PendingReply) => void;
  onStop?: () => void;
}) {
  const panelId = useId();
  // A pause that could not say what it asks takes a reply in words, like a question.
  const runApproval = paused?.asks?.kind === "approval" ? paused.asks : null;
  const runQuestion = !!paused && !runApproval;
  const [tab, setTab] = useState<DockTabId | null>(() => (paused ? (runApproval ? "approvals" : "questions") : null));

  // A run that pauses on the person opens its card: it cannot go on without them.
  const pausedOn = paused ? (paused.asks?.approval_id ?? "unknown") : null;
  const questions = useMemo(() => (runQuestion ? asked(paused?.asks?.call?.args, paused?.asks?.question) : []), [runQuestion, pausedOn]); // eslint-disable-line react-hooks/exhaustive-deps
  const picks = useQuestionPicks(questions, runQuestion ? pausedOn : null);
  useEffect(() => {
    if (pausedOn) setTab(runApproval ? "approvals" : "questions");
  }, [pausedOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes the open panel.
  useEffect(() => {
    if (!tab) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setTab(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab]);

  if (!paused) return null;

  const toggle = (t: DockTabId) => setTab((cur) => (cur === t ? null : t));

  return (
    <div className="relative w-full max-w-[720px] mx-auto min-w-0">
      {/* Rear layer: the category stays visible above the active card. */}
      <div className="mx-4 sm:mx-6 rounded-t-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface-2)] px-1.5 pt-1.5 pb-5 -mb-3 flex items-center gap-1 min-w-0 overflow-x-auto no-scrollbar">
        {runApproval && (
          <DockTab active={tab === "approvals"} onClick={() => toggle("approvals")} controls={panelId}>
            <Hand className="w-3.5 h-3.5 text-[var(--text-secondary)] shrink-0" strokeWidth={1.75} />
            <span>Approvals</span>
            <Badge tone="amber">1</Badge>
          </DockTab>
        )}
        {runQuestion && (
          <DockTab active={tab === "questions"} onClick={() => toggle("questions")} controls={panelId}>
            <MessageCircleQuestion className="w-3.5 h-3.5 text-[var(--text-secondary)] shrink-0" strokeWidth={1.75} />
            <span>Questions</span>
            <Badge tone="blue">{questions.length}</Badge>
          </DockTab>
        )}
        {questions.length > 1 ? (
          <QuestionSteps questions={questions} picks={picks} />
        ) : (
          <span className="hidden xl:block min-w-0 flex-1 pl-2 pr-2 text-right text-[10.5px] text-[var(--text-secondary)] tracking-[-0.005em] truncate">
            {`${agentName || "Agent"} needs you · 1 waiting`}
          </span>
        )}
      </div>
      {/* Middle layer: scroll long content, tuck its lower edge under the composer. */}
      <div className="relative mx-2 sm:mx-3">
        {tab && (
          <div
            key={tab}
            id={panelId}
            role="region"
            aria-label={tab === "approvals" ? "Approvals" : "Questions"}
            className="relative rounded-t-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] pb-3 -mb-3"
          >
            {/* questions take more room: four options, "Something else" and the buttons */}
            <div className={cn("overflow-y-auto overscroll-contain rounded-t-[14px]", tab === "questions" ? "max-h-[min(56dvh,440px)]" : "max-h-[min(36dvh,320px)]")}>
              {tab === "approvals" && runApproval && onAnswer && (
                <PendingApproval key={runApproval.approval_id} pending={runApproval} onAnswer={onAnswer} onStop={onStop} busy={paused.answering} />
              )}
              {tab === "questions" && runQuestion && onAnswer && (
                <PendingQuestion questions={questions} picks={picks} onAnswer={onAnswer} onStop={onStop} busy={paused.answering} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DockTab({ active, onClick, controls, children }: {
  active?: boolean;
  onClick?: () => void;
  controls: string;
  children?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-expanded={!!active}
      aria-controls={active ? controls : undefined}
      className={cn(
        "press relative shrink-0 whitespace-nowrap h-8 px-2 rounded-[8px] flex items-center gap-1.5 text-[11.5px] tracking-[-0.005em] min-w-0",
        active
          ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
          : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
      )}
    >
      {children}
    </button>
  );
}

function Badge({ tone, children }: { tone?: "amber" | "blue"; children?: React.ReactNode }) {
  const cls =
    tone === "amber"
      ? "bg-[var(--warn-bg)] text-[var(--warn-text)] ring-[var(--warn-ring)]"
      : "bg-[var(--info-bg)] text-[var(--info-text)] ring-[var(--info-ring)]";
  return (
    <span
      className={`min-w-[16px] h-[16px] px-1 rounded-full ring-1 text-[10px] font-mono font-medium tabular-nums flex items-center justify-center ${cls}`}
    >
      {children}
    </span>
  );
}

/** What a paused run asks: ask_user's questions, or for a run paused before they had options
    (or one that could not say), its question in words, answered in words. */
function asked(args: Record<string, unknown> | undefined, question: string | null | undefined): AskQuestion[] {
  const questions = questionsOf(args);
  return questions.length ? questions : [{ question: question || "The agent is waiting for your reply.", header: "", options: [] }];
}
